import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaMotorcycle, FaBoxOpen, FaUsers, FaCheckCircle, FaClock, FaTruck, FaChartLine, FaPlus } from 'react-icons/fa';
import { getAllOrders, getOrderStats } from '../../../../store/actions/orders';
import { getAllRiders } from '../../../../store/actions/riders';
import Container from './styles';

const DashContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector((s) => s.UserData);
  const orderData = useSelector((s) => s.OrderData || { data: [] });
  const riderData = useSelector((s) => s.RiderData || { data: [] });
  const orders = orderData.data || [];
  const riders = riderData.data || [];
  
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    inTransit: 0,
    delivered: 0,
    cancelled: 0,
    activeRiders: 0
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch orders and riders on mount
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          dispatch(getAllOrders(1, 100)),
          dispatch(getAllRiders()),
          fetchOrderStats()
        ]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    // Calculate stats from orders when they change
    if (orders.length > 0) {
      const pending = orders.filter(o => o.status === 'PENDING').length;
      const assigned = orders.filter(o => o.status === 'ASSIGNED').length;
      const pickedUp = orders.filter(o => o.status === 'PICKED_UP').length;
      const transit = orders.filter(o => o.status === 'IN_TRANSIT').length;
      const delivered = orders.filter(o => ['DELIVERED', 'COMPLETED'].includes(o.status)).length;
      const cancelled = orders.filter(o => o.status === 'CANCELLED').length;
      
      // Active riders are those with ACTIVE status
      const activeRidersCount = riders.filter(r => r.status === 'ACTIVE').length;
      
      setStats({
        totalOrders: orders.length,
        pendingOrders: pending,
        inTransit: assigned + pickedUp + transit,
        delivered: delivered,
        cancelled: cancelled,
        activeRiders: activeRidersCount
      });
    }
  }, [orders, riders]);

  const fetchOrderStats = async () => {
    try {
      const result = await dispatch(getOrderStats());
      if (result) {
        setStats(prev => ({
          ...prev,
          ...result
        }));
      }
    } catch (error) {
      console.error('Error fetching order stats:', error);
    }
  };

  const statCards = [
    {
      icon: FaBoxOpen,
      label: 'Total Orders',
      value: stats.totalOrders,
      change: `${stats.pendingOrders} pending`,
      color: '#10b981',
      onClick: () => navigate('/company/dashboard/orders')
    },
    {
      icon: FaClock,
      label: 'Pending Orders',
      value: stats.pendingOrders,
      change: 'Awaiting assignment',
      color: '#f59e0b',
      onClick: () => navigate('/company/dashboard/orders?status=pending')
    },
    {
      icon: FaTruck,
      label: 'In Transit',
      value: stats.inTransit,
      change: 'Active deliveries',
      color: '#3b82f6',
      onClick: () => navigate('/company/dashboard/orders?status=in_transit')
    },
    {
      icon: FaCheckCircle,
      label: 'Delivered',
      value: stats.delivered,
      change: 'Completed orders',
      color: '#6ABA6D',
      onClick: () => navigate('/company/dashboard/orders?status=delivered')
    },
  ];

  // Get recent 5 orders
  const recentOrders = orders.slice(0, 5);

  const getStatusColor = (status) => {
    const colors = {
      PENDING: '#FFA726',
      ASSIGNED: '#42A5F5',
      PICKED_UP: '#7E57C2',
      IN_TRANSIT: '#66BB6A',
      DELIVERED: '#4CAF50',
      COMPLETED: '#4CAF50',
      CANCELLED: '#EF5350',
    };
    return colors[status] || '#9E9E9E';
  };

  const getStatusLabel = (status) => {
    const labels = {
      PENDING: 'Pending',
      ASSIGNED: 'Assigned',
      PICKED_UP: 'Picked Up',
      IN_TRANSIT: 'In Transit',
      DELIVERED: 'Delivered',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
      if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="welcome-section">
        <div>
          <h1>Welcome back, {userData?.firstName || userData?.companyName || 'User'}!</h1>
          <p>Here's what's happening with your business today</p>
        </div>
        <button 
          className="primary-action-btn" 
          onClick={() => navigate('/company/dashboard/orders/create')}
        >
          <FaPlus />
          <span>Create Order</span>
        </button>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div 
            key={index} 
            className="stat-card" 
            style={{ borderLeftColor: stat.color }}
            onClick={stat.onClick}
          >
            <div className="stat-icon" style={{ background: `${stat.color}20`, color: stat.color }}>
              <stat.icon />
            </div>
            <div className="stat-content">
              <p className="stat-label">{stat.label}</p>
              <h2 className="stat-value">{stat.value}</h2>
              <p className="stat-change">{stat.change}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="content-grid">
        <div className="card recent-orders-card">
          <div className="card-header">
            <h3>Recent Orders</h3>
            <button className="view-all-btn" onClick={() => navigate('/company/dashboard/orders')}>
              View All
            </button>
          </div>
          <div className="orders-list">
            {recentOrders.length > 0 ? (
              recentOrders.map((order) => (
                <div 
                  key={order.id} 
                  className="order-item" 
                  onClick={() => navigate(`/company/dashboard/orders`)}
                >
                  <div className="order-info">
                    <p className="order-tracking">
                      <strong>#{order.trackingNumber || order.tracking_number}</strong>
                    </p>
                    <p className="order-customer">
                      {order.customerName || order.customer_name || 'Unknown Customer'}
                    </p>
                    <p className="order-address">
                      {order.deliveryAddress || order.delivery_address || 'No address'}
                    </p>
                  </div>
                  <div className="order-meta">
                    <span 
                      className="status-badge" 
                      style={{ 
                        background: getStatusColor(order.status),
                        color: '#fff'
                      }}
                    >
                      {getStatusLabel(order.status)}
                    </span>
                    <span className="order-time">
                      {formatDate(order.createdAt || order.created_at)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <FaBoxOpen style={{ fontSize: '3rem', opacity: 0.3 }} />
                <p>No orders yet</p>
                <button 
                  className="create-order-btn"
                  onClick={() => navigate('/company/dashboard/orders/create')}
                >
                  Create Your First Order
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="card quick-actions-card">
          <h3>Quick Actions</h3>
          <div className="actions-list">
            <button 
              className="action-btn"
              onClick={() => navigate('/company/dashboard/orders/create')}
            >
              <div className="action-icon" style={{ background: '#10b98120', color: '#10b981' }}>
                <FaPlus />
              </div>
              <div className="action-text">
                <span className="action-title">Create New Order</span>
                <span className="action-subtitle">Add a new delivery order</span>
              </div>
            </button>
            
            <button 
              className="action-btn"
              onClick={() => navigate('/company/dashboard/riders')}
            >
              <div className="action-icon" style={{ background: '#3b82f620', color: '#3b82f6' }}>
                <FaMotorcycle />
              </div>
              <div className="action-text">
                <span className="action-title">Manage Riders</span>
                <span className="action-subtitle">{stats.activeRiders} active rider{stats.activeRiders !== 1 ? 's' : ''}</span>
              </div>
            </button>
            
            <button 
              className="action-btn"
              onClick={() => navigate('/company/dashboard/staff')}
            >
              <div className="action-icon" style={{ background: '#f59e0b20', color: '#f59e0b' }}>
                <FaUsers />
              </div>
              <div className="action-text">
                <span className="action-title">Manage Staff</span>
                <span className="action-subtitle">View and manage staff</span>
              </div>
            </button>
            
            <button 
              className="action-btn"
              onClick={() => navigate('/company/dashboard/statistics')}
            >
              <div className="action-icon" style={{ background: '#8b5cf620', color: '#8b5cf6' }}>
                <FaChartLine />
              </div>
              <div className="action-text">
                <span className="action-title">View Statistics</span>
                <span className="action-subtitle">Analytics and reports</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </Container>
  )
}

export default DashContent
